import elearningApi from 'api/elearningApi';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Alert, Container, Pagination, Modal, Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Timer from '../components/Timer';
import { toastWrapper } from 'utils';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectElearningData, selectElearningUser } from 'store/elearning.slice';
import TimeExceedWarning from '../components/TimeExceedWarning';
import { replaceImageSrcWithMoodleUrl } from 'utils/elearning.utils';
import LoadingSpinner from '../components/LoadingSpinner';
import BackButton from 'components/BackButton';
import useSingleTab from 'hooks/useSingleTab';
import { CAP_CLEARED_EVENT, notifyDailyLimitReached, responseHitDailyLimit } from 'utils/daily-limit-event.util';

// Dedupe toast 429 của heartbeat lưu tiến độ — chỉ hiện tối đa 1 lần/60s để
// không spam khi nhiều tick liên tiếp bị rate-limit.
let _lastBookRateLimitToastAt = 0;

const ElearningStudentBookPage = () => {
  useSingleTab('/elearning/student/book', 'elearning_module');
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [moduleTimeData, setModuleTimeData] = useState({});
  const [currentChapterId, setCurrentChapterId] = useState(null);
  const [timestart, setTimestart] = useState(Date.now());
  const [completedChapters, setCompletedChapters] = useState([]);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [drivingStudent, setDrivingStudent] = useState(null);
  const [isBookDataLoaded, setIsBookDataLoaded] = useState(false);
  const [showModal, setShowModal] = useState(true);
  // Khi BE trả 403 CAP_SUSPENDED → set true → block mọi save call kế tiếp
  // + show modal cho HV biết. Reload trang sẽ reset.
  const capSuspendedRef = useRef(false);
  // Tăng khi overlay khoá đóng → chạy lại effect ghi giờ.
  const [capResumeTick, setCapResumeTick] = useState(0);
  const timeSpentIntervalRef = useRef(null);
  const timestartRef = useRef(Date.now());
  const totalTimeSpentRef = useRef(0);
  const drivingStudentRef = useRef(null);
  const isCompletingRef = useRef(false);
  const moduleTimeDataRef = useRef({});
  const moduleIntervalRef = useRef({});

  // Listen global cap-suspended event từ axiosClient interceptor.
  // Set ref TRƯỚC state để các save call đang inflight (vd 30s heartbeat)
  // có thể guard bằng capSuspendedRef.current để skip.
  useEffect(() => {
    // Thông báo do CapSuspendedOverlay (gắn ở App) hiện — ở đây chỉ dừng ghi giờ.
    const onCapSuspended = () => {
      capSuspendedRef.current = true;
      window.__capSuspendedHandled = true; // tell axiosClient không cần fallback alert
      // Pause heartbeat interval ngay
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
    };
    // Overlay đóng → thử ghi giờ lại (còn khoá thì lượt đầu nhận 403 và dừng).
    const onCapCleared = () => {
      capSuspendedRef.current = false;
      setCapResumeTick((t) => t + 1);
    };
    window.addEventListener('cap-suspended', onCapSuspended);
    window.addEventListener(CAP_CLEARED_EVENT, onCapCleared);
    return () => {
      window.removeEventListener('cap-suspended', onCapSuspended);
      window.removeEventListener(CAP_CLEARED_EVENT, onCapCleared);
    };
  }, []);
  const [isModuleTimeLoaded, setIsModuleTimeLoaded] = useState(false);
  const [retakeTarget, setRetakeTarget] = useState(null);
  const [restudyKey, setRestudyKey] = useState(0);
  const [showRestudyModal, setShowRestudyModal] = useState(false);
  const [currentMapa, setCurrentMapa] = useState([]);
  const [mapaLength, setMapaLength] = useState(0);
  const [autoPageTurning, setAutoPageTurning] = useState(
    () => localStorage.getItem('book_auto_page') !== 'false'
  );
  const autoPageTurningRef = useRef(localStorage.getItem('book_auto_page') !== 'false');
  const [isManualPaused, setIsManualPaused] = useState(false);
  const isManualPausedRef = useRef(false);
  const [isDailyLimitReached, setIsDailyLimitReached] = useState(false);
  const isDailyLimitReachedRef = useRef(false);
  // Mirror Redux `isLimitExceeded` (server-truth từ StudentElearningLayout poll)
  // để các save call inflight đọc được giá trị mới nhất mà không cần state stale.
  const isLimitExceededRef = useRef(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const isTabHiddenRef = useRef(false);
  const sessionTimeRef = useRef(0);
  const timeLimitRef = useRef({ timeLimitPerDay: null, totalTodayTime: null });
  // Phase 2D follow-on: moodleToken không còn ở localStorage (step 5).
  // Truớc đây append `?token=${moodleToken}` vào URL Moodle pluginfile;
  // bây giờ FE giữ NGUYÊN URL Moodle, fetch qua BE proxy
  // `/elearning/book/pluginfile?fileurl=...` (proxy attach wstoken
  // server-side). Xem loadContent effect dưới.
  const [searchParams] = useSearchParams();
  const fileUrls = useMemo(() => {
    const urls = searchParams.get('urls')?.split(',') || [];
    return urls.map((url) => url.split('?')[0]);
  }, [searchParams]);
  const elearningData = useSelector(selectElearningData);
  const student = useSelector(selectElearningUser);
  const { isLimitExceeded, timeLimitPerDay, totalTodayTime } = elearningData;
  timeLimitRef.current = { timeLimitPerDay, totalTodayTime };
  autoPageTurningRef.current = autoPageTurning;
  isLimitExceededRef.current = isLimitExceeded;
  const isPaused = isManualPaused || isDailyLimitReached || isTabHidden;

  const moduleId = Number(searchParams.get('m'));

  const autoAdvanceTimeoutRef = useRef(null);
  // Đánh dấu chapter nào đã trigger UI completion (toast + auto-advance) để không fire 2 lần
  // khi cả heartbeat và handleTimeUp cùng phát hiện completed.
  const autoAdvanceFiredForRef = useRef(null);
  // Mirror completedChapters vào ref để đọc trong callback bất đồng bộ mà không cần state stale.
  const completedChaptersRef = useRef([]);
  useEffect(() => {
    completedChaptersRef.current = completedChapters;
  }, [completedChapters]);

  // Lấy drivingStudent từ redux `student` nếu có, fallback localStorage
  useEffect(() => {
    if (student && student?._id) {
      setDrivingStudent(student._id);
      drivingStudentRef.current = student._id;
      return;
    }

    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user?._id) {
          setDrivingStudent(user._id);
          drivingStudentRef.current = user._id;
        }
      } catch (err) {
        console.warn('Failed to parse localStorage user', err);
      }
    }
  }, [student]);

  const handleTogglePause = useCallback(() => {
    setIsManualPaused((prev) => {
      const next = !prev;
      isManualPausedRef.current = next;
      return next;
    });
  }, []);

  const cancelAutoAdvance = useCallback(() => {
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
  }, []);

  // Trigger toast + auto-advance khi server xác nhận chapter completed.
  // Idempotent theo chapterId: gọi nhiều lần (heartbeat + handleTimeUp) chỉ chạy 1.
  const triggerCompletionUI = useCallback((chapterId) => {
    if (!chapterId || autoAdvanceFiredForRef.current === chapterId) return;
    autoAdvanceFiredForRef.current = chapterId;

    const alreadyInList = completedChaptersRef.current.includes(chapterId);
    const totalAfter = alreadyInList
      ? completedChaptersRef.current.length
      : completedChaptersRef.current.length + 1;

    if (totalAfter >= fileUrls.length) {
      toastWrapper('Bạn đã hoàn thành tất cả các chương.', 'success');
      return;
    }

    if (!autoPageTurningRef.current || isDailyLimitReachedRef.current) {
      toastWrapper('Bạn đã hoàn thành chương này.', 'success');
      return;
    }

    toastWrapper(
      'Bạn đã hoàn thành chương này. Đang chuyển sang chương kế tiếp.',
      'success'
    );
    cancelAutoAdvance();
    autoAdvanceTimeoutRef.current = setTimeout(() => {
      autoAdvanceTimeoutRef.current = null;
      setCurrentIndex((prev) =>
        prev < fileUrls.length - 1 ? prev + 1 : prev
      );
    }, 2000);
  }, [fileUrls.length, cancelAutoAdvance]);

  // Cancel auto-advance khi đổi chapter (auto hoặc manual) và khi unmount.
  // Reset cờ idempotent để chapter mới có thể trigger lần đầu.
  useEffect(() => {
    autoAdvanceFiredForRef.current = null;
    return () => {
      cancelAutoAdvance();
    };
  }, [currentChapterId, cancelAutoAdvance]);

  // Lấy dữ liệu thời gian xem từ server khi thay đổi chapter
  useEffect(() => {
    const loadStudentBookData = async () => {
      const elearningUserId = elearningData?.elearningUser?.elearningUserId;

      if (!currentChapterId || !elearningUserId || !isModuleTimeLoaded) {
        setIsBookDataLoaded(false);
        return;
      }
      // Đã khoá ngày → không tạo doc mới và không retry Moodle sync (cả 2 đều
      // là save call có side effect tạo/cập nhật mapa). Chỉ đọc dữ liệu hiện có.
      const limitLocked = isLimitExceededRef.current;

      try {
        const res = await elearningApi.getStudentBookData(
          elearningUserId,
          currentChapterId,
          moduleId
        );

        // Nếu dữ liệu trả về trống thì tạo mới
        // Server trả về array, lấy phần tử đầu tiên
        const bookData = Array.isArray(res?.data) ? res?.data[0] : res?.data;
        
        if (!bookData || Object.keys(bookData).length === 0) {
          // BUG FIX (2026-04-26): trước đây gọi với mapa client-generated
          // `mapa: [Math.floor(Date.now()/1000)]` — nếu request này race với
          // beforeunload/refresh/strict-mode, server `findOne+create` 2 step
          // tạo 2 ghost records. Đó là root cause của 11,673 duplicate trong
          // production. Chuyển sang `useServerTime: true` để server tự stamp
          // timestamp và xử lý atomic E11000 với unique compound index.
          if (!limitLocked) {
            await elearningApi.saveStudentBookData({
              drivingStudent,
              elearningUserId: elearningUserId,
              chapterId: currentChapterId,
              moduleId,
              moduleType: 'book',
              useServerTime: true,
            });
          }
          setTotalTimeSpent(0);
          totalTimeSpentRef.current = 0;
          setRetakeTarget(null);
        } else if (bookData?.timeSpentInSecond !== undefined) {
          // Server là single source of truth: tính thời gian từ mapa.length * interval
          // (khớp với công thức completion server-side: mapa.length >= ceil(readingTime*60/interval))
          const mapaLen = Array.isArray(bookData?.mapa) ? bookData.mapa.length : 0;
          const interval = moduleIntervalRef.current[currentChapterId] || 30;
          const effectiveTime = mapaLen * interval;
          setTotalTimeSpent(effectiveTime);
          totalTimeSpentRef.current = effectiveTime;
          // Lưu mapa để hiển thị ngày học lại
          if (Array.isArray(bookData?.mapa)) {
            setCurrentMapa(bookData.mapa);
          }
          // Lưu retakeTarget nếu có
          setRetakeTarget(bookData?.retakeTarget || null);
          // Nếu chương đã hoàn thành trước đó, thêm vào completedChapters
          if (bookData?.completed) {
            setCompletedChapters((prev) =>
              prev.includes(currentChapterId) ? prev : [...prev, currentChapterId]
            );

            // Backward-compat: record cũ completed=true nhưng chưa được Moodle sync
            // (cơ chế client-side cũ thất bại). Bắn 1 upsert no-op để server tự retry
            // syncBookCompletionToMoodle. Server idempotent, không tốn gì.
            // Skip khi đã khoá ngày — không cần thiết và tránh trigger thêm mapa.
            if (!bookData?.moodleSyncedAt && !limitLocked) {
              elearningApi
                .saveStudentBookData({
                  drivingStudent,
                  elearningUserId,
                  chapterId: currentChapterId,
                  moduleId,
                  moduleType: 'book',
                  useServerTime: true,
                })
                .catch(() => {});
            }
          }
        } else {
          setTotalTimeSpent(0);
          totalTimeSpentRef.current = 0;
          setRetakeTarget(null);
        }

        // Reset timestart khi load chapter mới
        const newTime = Date.now();
        setTimestart(newTime);
        timestartRef.current = newTime;

        // Đánh dấu dữ liệu đã load xong, giờ có thể bắt đầu timer
        setIsBookDataLoaded(true);
      } catch (error) {
        console.error('Error loading student book data:', error);
        setTotalTimeSpent(0);
        totalTimeSpentRef.current = 0;
        setRetakeTarget(null);
        const newTime = Date.now();
        setTimestart(newTime);
        timestartRef.current = newTime;
        // Vẫn set flag để timer bắt đầu ngay cả khi có lỗi
        setIsBookDataLoaded(true);
      }
    };

    setIsBookDataLoaded(false);
    setTotalTimeSpent(0);
    totalTimeSpentRef.current = 0;
    setCurrentMapa([]);
    setMapaLength(0);
    isCompletingRef.current = false; // Reset khi chuyển chapter mới
    loadStudentBookData();
  }, [currentChapterId, elearningData?.elearningUser?.elearningUserId, moduleId, drivingStudent, isModuleTimeLoaded]);

  // Reset state khi chuyển sang bài book khác
  useEffect(() => {
    setCurrentIndex(0);
    setHtmlContent('');
    setCompletedChapters([]);
    setCurrentChapterId(null);
    setModuleTimeData({});
    setIsModuleTimeLoaded(false);
    setIsBookDataLoaded(false);
    setTotalTimeSpent(0);
    totalTimeSpentRef.current = 0;
    sessionTimeRef.current = 0;
    setCurrentMapa([]);
    setRetakeTarget(null);
    setShowModal(true);
    setIsDailyLimitReached(false);
    isDailyLimitReachedRef.current = false;
    setIsManualPaused(false);
    isManualPausedRef.current = false;
    timestartRef.current = Date.now();
  }, [moduleId]);

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      const fileUrl = fileUrls[currentIndex];
      if (!fileUrl) return;

      const chapterId = Number(fileUrl.split('/').at(-2));
      setCurrentChapterId(chapterId);
      const newTime = Date.now();
      setTimestart(newTime);
      timestartRef.current = newTime;

      try {
        // Phase 2D follow-on: fetch chapter HTML qua BE proxy thay vì
        // gọi thẳng Moodle pluginfile.php (đã fail invalidtoken sau khi
        // strip moodleToken khỏi browser). BE rewrite img src trong
        // HTML thành cùng proxy URL → image cũng đi qua đây.
        const text = await elearningApi.fetchBookChapterHtml(fileUrl);
        setHtmlContent(text);
      } catch (error) {
        console.error('Error fetching HTML content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [currentIndex, fileUrls]);



  // Gửi dữ liệu mỗi 1 phút (60 giây) - sử dụng useRef để tránh vòng lặp vô hạn
  useEffect(() => {
    // Khi server-truth lock flip (`isLimitExceeded`), Redux đẩy về true → tear down
    // interval ngay. Trước đó interval không có dep này nên cứ chạy tiếp tới khi
    // BE trả 403 CAP_SUSPENDED — đó chính là root cause mapa vẫn cộng sau khi UI khoá.
    if (!currentChapterId || completedChapters.includes(currentChapterId) || isLimitExceeded) {
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
      return;
    }

    // Gửi heartbeat tới server — server tự sinh mapa entry + timestamp
    const saveData = async () => {
      if (isCompletingRef.current || !drivingStudentRef.current || !elearningData?.elearningUser?.elearningUserId) {
        return;
      }
      if (isManualPausedRef.current || isDailyLimitReachedRef.current || isTabHiddenRef.current) return;
      if (capSuspendedRef.current) return; // BE đã 403 — skip mọi save heartbeat
      // Server-truth lock — chặn cả request đang inflight ở vòng tick kế tiếp.
      // `isDailyLimitReachedRef` chỉ flip sau 1 save thành công + dựa trên Redux
      // `totalTodayTime` (stale), không đáng tin so với `isLimitExceeded` từ poll.
      if (isLimitExceededRef.current) return;
      try {
        const res = await elearningApi.saveStudentBookData({
          drivingStudent: drivingStudentRef.current,
          elearningUserId: elearningData?.elearningUser?.elearningUserId,
          chapterId: currentChapterId,
          moduleId,
          moduleType: 'book',
          useServerTime: true,
        });
        if (responseHitDailyLimit(res)) notifyDailyLimitReached();

        const doc = res?.data;
        if (doc) {
          if (Array.isArray(doc.mapa)) {
            setCurrentMapa(doc.mapa);
            setMapaLength(doc.mapa.length);
          }
          setTotalTimeSpent(doc.timeSpentInSecond || 0);
          totalTimeSpentRef.current = doc.timeSpentInSecond || 0;

          if (doc.completed) {
            setCompletedChapters((prev) =>
              prev.includes(currentChapterId) ? prev : [...prev, currentChapterId]
            );
            // Heartbeat có thể về đích trước handleTimeUp (server đếm bằng mapa,
            // không phụ thuộc Timer wall-clock của client). Trigger completion UI
            // ngay tại đây để auto-advance không bị bỏ lỡ.
            triggerCompletionUI(currentChapterId);
          }
        }

        // Kiểm tra giới hạn thời gian học trong ngày
        const { timeLimitPerDay: limit, totalTodayTime: todayTime } = timeLimitRef.current;
        if (limit && limit > 0 && (todayTime || 0) >= limit) {
          isDailyLimitReachedRef.current = true;
          setIsDailyLimitReached(true);
          toastWrapper('Bạn đã học đủ thời gian cho hôm nay. Bộ đếm đã tạm dừng.', 'warning');
        }
      } catch (error) {
        console.error('Error saving heartbeat:', error?.response?.data || error?.message || error);
        // Còn khoá: dừng ghi ngay (sự kiện cap-suspended của axiosClient có thể đang bị chống lặp).
        if (error?.response?.data?.code === 'CAP_SUSPENDED') {
          capSuspendedRef.current = true;
          if (timeSpentIntervalRef.current) {
            clearInterval(timeSpentIntervalRef.current);
            timeSpentIntervalRef.current = null;
          }
          return;
        }
        // 429 (lưu tiến độ quá nhanh ở chương này) → báo rõ cho HV thay vì
        // im lặng, kèm thời gian chờ. Dedupe 60s để không spam toast.
        if (error?.response?.status === 429) {
          const now = Date.now();
          if (now - _lastBookRateLimitToastAt > 60000) {
            _lastBookRateLimitToastAt = now;
            const d = error?.response?.data;
            const base = d?.message || 'Đang lưu tiến độ quá nhanh. Vui lòng đợi giây lát.';
            toastWrapper(d?.retryAfter ? `${base} (thử lại sau ${d.retryAfter}s)` : base, 'warning');
          }
        }
      }
    };

    const intervalMs = (moduleIntervalRef.current[currentChapterId] || 30) * 1000;
    timeSpentIntervalRef.current = setInterval(saveData, intervalMs);

    // Khi tab ẩn → dừng đếm + dừng timer, khi hiện lại → reset timestamp để không tính thời gian ẩn
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isTabHiddenRef.current = true;
        setIsTabHidden(true);
      } else {
        isTabHiddenRef.current = false;
        setIsTabHidden(false);
        timestartRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeSpentIntervalRef.current) {
        clearInterval(timeSpentIntervalRef.current);
        timeSpentIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentChapterId, completedChapters, moduleId, elearningData?.elearningUser?.elearningUserId, triggerCompletionUI, isLimitExceeded, capResumeTick]);

  // Lưu dữ liệu khi người dùng rời khỏi trang (dùng fetch + keepalive để đảm bảo request hoàn tất)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Đã khoá ngày → không gửi thêm save cuối khi đóng/reload tab.
      if (isLimitExceededRef.current) return;
      if (currentChapterId && !completedChapters.includes(currentChapterId) && !isCompletingRef.current) {
        const hostname = window.location.hostname;
        const apiBaseUrl = hostname?.split('.')?.includes('lms2')
          ? process.env.REACT_APP_API_2_URL
          : process.env.REACT_APP_API_URL;
        // Cùng thứ tự với axiosClient: sessionToken trước. Route bắt token từ
        // 17/10/2026 — gửi `user-jwt-tk` (thường rỗng ở LMS) sẽ bị 401.
        const token = localStorage.getItem('sessionToken') || localStorage.getItem('user-jwt-tk');
        const moodleToken = localStorage.getItem('moodleToken');

        fetch(`${apiBaseUrl}/elearning-student-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            // Forward Moodle wstoken để backend gọi mod_book_view_book dưới tên student.
            // fetch() bypass axios interceptor nên phải set thủ công ở đây.
            ...(moodleToken ? { 'X-Moodle-Token': moodleToken } : {}),
          },
          body: JSON.stringify({
            drivingStudent: drivingStudentRef.current,
            elearningUserId: elearningData?.elearningUser?.elearningUserId,
            chapterId: currentChapterId,
            moduleId,
            moduleType: 'book',
            useServerTime: true,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentChapterId, completedChapters, moduleId, elearningData?.elearningUser?.elearningUserId]);

  useEffect(() => {
    const getModuleTimeData = async () => {
      try {
        const res = await elearningApi.getModuleTime(moduleId);
        const moduleTimeMap = {};
        const intervalMap = {};
        res?.data?.forEach((item) => {
          moduleTimeMap[item?.chapterId] = item?.readingTime;
          intervalMap[item?.chapterId] = item?.interval || 30;
        });
        setModuleTimeData(moduleTimeMap);
        moduleTimeDataRef.current = moduleTimeMap;
        moduleIntervalRef.current = intervalMap;
        setIsModuleTimeLoaded(true);
      } catch (err) {
        console.error('Error fetching module time:', err);
        setIsModuleTimeLoaded(true);
      }
    };

    if (moduleId) {
      setIsModuleTimeLoaded(false);
      getModuleTimeData();
    }
  }, [moduleId]);

  const handleTimeUp = useCallback(async () => {
    if (!currentChapterId || isCompletingRef.current) return;

    // Đánh dấu đang hoàn thành để ngăn auto-save ghi đè
    isCompletingRef.current = true;

    // Dừng interval ngay lập tức
    if (timeSpentIntervalRef.current) {
      clearInterval(timeSpentIntervalRef.current);
      timeSpentIntervalRef.current = null;
    }

    // Gửi heartbeat cuối - server là single source of truth cho completed + Moodle sync
    let serverDoc = null;
    if (capSuspendedRef.current) {
      isCompletingRef.current = false;
      return; // axiosClient interceptor đã show modal — không cần gọi nữa
    }
    try {
      const res = await elearningApi.saveStudentBookData({
        drivingStudent: drivingStudentRef.current,
        elearningUserId: elearningData.elearningUser.elearningUserId,
        chapterId: currentChapterId,
        moduleId,
        moduleType: 'book',
        useServerTime: true,
      });
      serverDoc = res?.data;
    } catch (error) {
      console.error('Error sending final heartbeat:', error);
      isCompletingRef.current = false;
      // Skip toast khi CAP_SUSPENDED — modal đã cover.
      if (error?.response?.data?.code !== 'CAP_SUSPENDED') {
        toastWrapper('Không thể xác nhận hoàn thành chương. Vui lòng thử lại.', 'error');
      }
      return;
    }

    if (Array.isArray(serverDoc?.mapa)) {
      setCurrentMapa(serverDoc.mapa);
      setMapaLength(serverDoc.mapa.length);
    }
    setTotalTimeSpent(serverDoc?.timeSpentInSecond || 0);
    totalTimeSpentRef.current = serverDoc?.timeSpentInSecond || 0;

    // Server chưa xác nhận completed → để Timer đếm ngược phần còn lại, không tự override
    if (serverDoc?.completed !== true) {
      isCompletingRef.current = false;
      return;
    }

    // Server xác nhận completed → cập nhật UI (server đã tự lo Moodle sync).
    // Updater pure, không có side effect bên trong.
    setCompletedChapters((prev) =>
      prev.includes(currentChapterId) ? prev : [...prev, currentChapterId]
    );
    // Toast + auto-advance đặt riêng, idempotent với heartbeat (cùng trigger có thể đã chạy).
    triggerCompletionUI(currentChapterId);
  }, [currentChapterId, moduleId, elearningData?.elearningUser?.elearningUserId, triggerCompletionUI]);

  // Lấy danh sách ngày + thời gian học từ mapa (UTC+7)
  const mapaDates = useMemo(() => {
    if (!Array.isArray(currentMapa) || currentMapa.length === 0) return [];
    const interval = moduleIntervalRef.current[currentChapterId] || 30;
    const dateMap = {};
    currentMapa.forEach((ts) => {
      if (!ts || ts === 0) return;
      const d = new Date(ts * 1000 + 7 * 60 * 60 * 1000);
      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const key = `${yyyy}-${mm}-${dd}`;
      dateMap[key] = (dateMap[key] || 0) + 1;
    });
    return Object.entries(dateMap)
      .map(([date, count]) => ({ date, seconds: count * interval }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [currentMapa, currentChapterId]);

  // Kiểm tra hoàn thành: theo trạng thái completed từ server
  const isReallyCompleted = useMemo(() => {
    return completedChapters?.includes(currentChapterId) || false;
  }, [completedChapters, currentChapterId]);

  const [restudyLoading, setRestudyLoading] = useState(false);

  const openRestudyModal = useCallback(async () => {
    if (!currentChapterId) return;
    setShowRestudyModal(true);
    const elearningUserId = elearningData?.elearningUser?.elearningUserId;
    if (!elearningUserId) return;
    setRestudyLoading(true);
    try {
      const res = await elearningApi.getStudentBookData(elearningUserId, currentChapterId, moduleId);
      const bookData = Array.isArray(res?.data) ? res?.data[0] : res?.data;
      if (bookData) {
        if (Array.isArray(bookData.mapa)) {
          setCurrentMapa(bookData.mapa);
        }
        setRetakeTarget(bookData.retakeTarget || null);
      }
    } catch (err) {
      console.error('Error refreshing mapa:', err);
    } finally {
      setRestudyLoading(false);
    }
  }, [currentChapterId, moduleId, elearningData?.elearningUser?.elearningUserId]);

  const handleRestudy = useCallback(
    async (retakeDate) => {
      if (!currentChapterId) return;

      // Xoa toan bo can xac nhan them
      if (!retakeDate) {
        if (
          !window.confirm(
            'Bạn có chắc muốn học lại toàn bộ? Dữ liệu học sẽ bị ghi đè với số thời gian tương ứng.'
          )
        )
          return;
      }

      try {
        const payload = {
          drivingStudent: drivingStudentRef.current,
          elearningUserId: elearningData?.elearningUser?.elearningUserId,
          chapterId: currentChapterId,
          moduleId,
          moduleType: 'book',
          retake: true,
        };

        if (retakeDate) {
          // Partial retake: gửi retakeDate dạng ISO cho backend xử lý gradual replacement
          // "YYYY-MM-DD" → "YYYY-MM-DDT00:00:00.000Z"
          payload.retakeDate = `${retakeDate}T00:00:00.000Z`;
        }

        const res = await elearningApi.saveStudentBookData(payload);
        const updatedData = res?.data;

        if (retakeDate) {
          // Partial retake: backend đã lưu retakeTarget, đọc lại data mới từ response
          const interval = moduleIntervalRef.current[currentChapterId] || 30;
          const remaining = updatedData?.retakeTarget?.remaining || 0;
          if (updatedData) {
            if (Array.isArray(updatedData.mapa)) {
              setCurrentMapa(updatedData.mapa);
            }
            if (updatedData.retakeTarget) {
              setRetakeTarget(updatedData.retakeTarget);
            }
            // Tính thời gian hiệu lực = tổng mapa - phần cần retake (remaining entries sẽ bị thay thế)
            const mapaLen = Array.isArray(updatedData.mapa) ? updatedData.mapa.length : 0;
            const effectiveMapa = Math.max(0, mapaLen - remaining);
            const newTimeSpent = effectiveMapa * interval;
            setTotalTimeSpent(newTimeSpent);
            totalTimeSpentRef.current = newTimeSpent;
          }

          setCompletedChapters((prev) =>
            prev.filter((id) => id !== currentChapterId)
          );
          isCompletingRef.current = false;
          const newTime = Date.now();
          setTimestart(newTime);
          timestartRef.current = newTime;

          setIsBookDataLoaded(false);
          setRestudyKey((prev) => prev + 1);
          setTimeout(() => setIsBookDataLoaded(true), 100);
          setShowRestudyModal(false);

          toastWrapper(
            `Đang học lại ngày ${retakeDate.split('-').reverse().join('/')}.`,
            'success'
          );
        } else {
          // Full retake: server reset mapa=[], timeSpentInSecond=0, completed=false
          setCurrentMapa([]);
          setRetakeTarget(null);
          setCompletedChapters((prev) =>
            prev.filter((id) => id !== currentChapterId)
          );
          setTotalTimeSpent(0);
          totalTimeSpentRef.current = 0;
          isCompletingRef.current = false;
          const newTime = Date.now();
          setTimestart(newTime);
          timestartRef.current = newTime;

          setIsBookDataLoaded(false);
          setRestudyKey((prev) => prev + 1);
          setTimeout(() => setIsBookDataLoaded(true), 100);
          setShowRestudyModal(false);

          toastWrapper(
            'Đang bắt đầu học lại.',
            'success'
          );
        }
      } catch (error) {
        console.error('Error sending retake request:', error);
        toastWrapper(
          'Có lỗi xảy ra khi học lại. Vui lòng thử lại.',
          'error'
        );
      }
    },
    [currentChapterId, moduleId, elearningData?.elearningUser?.elearningUserId]
  );

  if (isLimitExceeded) {
    return (
      <TimeExceedWarning
        timeLimitPerDay={timeLimitPerDay}
        totalTodayTime={totalTodayTime}
      />
    );
  }

  // Kiểm tra thời gian khai giảng - bế giảng
  // const enrollmentDate = student?.course?.enrollmentDate;
  // const graduationDate = student?.course?.graduationDate;
  // const now = new Date();
  // const isOutsideCourseDate =
  //   (enrollmentDate && new Date(enrollmentDate) > now) ||
  //   (graduationDate && new Date(graduationDate) < now);

  // if (isOutsideCourseDate) {
  //   return (
  //     <Container className='my-4 text-center'>
  //       <BackButton />
  //       <Alert variant='warning' className='mt-3'>
  //         <Alert.Heading>Ngoài thời gian học</Alert.Heading>
  //         <p className='mb-0'>
  //           Bạn chỉ có thể xem bài học trong khoảng thời gian từ{' '}
  //           <strong>
  //             {enrollmentDate
  //               ? new Date(enrollmentDate).toLocaleDateString('en-GB')
  //               : '---'}
  //           </strong>{' '}
  //           đến{' '}
  //           <strong>
  //             {graduationDate
  //               ? new Date(graduationDate).toLocaleDateString('en-GB')
  //               : '---'}
  //           </strong>.
  //         </p>
  //       </Alert>
  //     </Container>
  //   );
  // }

  return (
    <Styles>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Hướng dẫn vào học</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ lineHeight: 1.8 }}>
            <p>
              <strong>1. Bài học bao gồm nhiều chương</strong>
              <br />
              Vui lòng xem hết thời gian đếm ngược của các chương để được ghi
              nhận.
            </p>
            <p>
              <strong>2. Cách ghi nhận thời gian học</strong>
              <br />
              - Thời gian học sẽ được ghi nhận vào báo cáo sau khi học viên hoàn
              tất toàn bộ các chương học.
              <br />
              - Lịch sử thời gian học sẽ được lưu lại.
            </p>
            <p>
              <strong>3. Lưu ý quan trọng</strong>
              <br />
              - Không rời khỏi màn hình học (chuyển tab, thu nhỏ trình duyệt, tắt màn hình) trong khi đang học.
              <br />
              - Việc rời màn hình sẽ khiến thời gian học không được ghi nhận.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={() => setShowModal(false)}>
            Đã hiểu
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal chọn ngày học lại */}
      <Modal show={showRestudyModal} onHide={() => setShowRestudyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Chọn ngày muốn học lại</Modal.Title>
        </Modal.Header>
        {retakeTarget && retakeTarget.remaining > 0 && (
          <div style={{ background: '#fff7ed', borderBottom: '1px solid #fed7aa', padding: '10px 16px', fontSize: 13, lineHeight: 1.6 }}>
            <strong style={{ color: '#c2410c' }}>Đang học lại</strong>
            {retakeTarget.date && (
              <> ngày <strong>{new Date(retakeTarget.date).toLocaleDateString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' })}</strong></>
            )}
          </div>
        )}
        <Modal.Body style={{ padding: 0 }}>
          <div style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', padding: '10px 16px', fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
            Khi bạn chọn học lại một ngày, thời gian học của ngày đó sẽ <strong>không bị xoá</strong> mà được <strong>trừ dần</strong> khi bạn học lại. Mỗi khoảng thời gian bạn học hôm nay sẽ thay thế một khoảng tương ứng của ngày cần học lại.<br/>Nếu ngày cần học lại không có, hãy chọn Học lại toàn bộ với số thời gian bạn muốn, không bắt buộc học hết thời gian đếm ngược.
          </div>
          {restudyLoading ? (
            <div className='text-center py-4'>
              <LoadingSpinner />
            </div>
          ) : mapaDates.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Ngày</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>Thời gian</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}></th>
                </tr>
              </thead>
              <tbody>
                {mapaDates.map(({ date, seconds }) => {
                  const mm = Math.floor(seconds / 60);
                  const ss = seconds % 60;
                  const timeStr = mm > 0
                    ? `${mm} phút ${ss > 0 ? `${ss} giây` : ''}`
                    : `${ss} giây`;
                  return (
                    <tr key={date} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 16px' }}>
                        {date.split('-').reverse().join('/')}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {timeStr}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={() => handleRestudy(date)}
                          style={{ fontSize: 12 }}
                        >
                          Học lại
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: 14, color: '#6b7280', margin: 0, padding: 16 }}>
              {totalTimeSpent > 0
                ? 'Chương này được học trước khi có tính năng ghi nhận theo ngày. Hãy nhấn "Học lại tất cả" để học lại đủ số thời gian mong muốn. Dữ liệu học của bạn sẽ không bị mất.'
                : 'Chưa có dữ liệu học.'}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#374151' }}>
            Tổng: <strong>
              {(() => {
                const total = mapaDates.reduce((sum, d) => sum + d.seconds, 0);
                const h = Math.floor(total / 3600);
                const m = Math.floor((total % 3600) / 60);
                const s = total % 60;
                if (h > 0) return `${h} giờ ${m > 0 ? `${m} phút` : ''}`;
                if (m > 0) return `${m} phút ${s > 0 ? `${s} giây` : ''}`;
                return `${s} giây`;
              })()}
            </strong>
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setShowRestudyModal(false)}
            >
              Đóng
            </Button>
            <Button
              variant='danger'
              size='sm'
              onClick={() => handleRestudy(null)}
            >
              Học lại tất cả
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Container className='my-2'>
        <BackButton />
        <div
          style={{
            height: '78vh',
            overflowY: 'scroll',
            padding: '5%',
            backgroundColor: '#fff',
          }}
        >
          {isLoading || !isBookDataLoaded ? (
            <div className='text-center py-5'>
              <LoadingSpinner />
            </div>
          ) : (
            <div
              className='book-chapter-content'
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}
        </div>
        <div
          style={{
            position: 'fixed',
            bottom: 70,
            left: 0,
            right: 0,
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div style={{ maxWidth: 400 }}>
            {!isReallyCompleted &&
            isBookDataLoaded ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                {moduleTimeData[currentChapterId] ? (
                  <Timer
                    key={`${currentChapterId}-${totalTimeSpent}-${restudyKey}`}
                    timelimit={
                      moduleTimeData[currentChapterId] -
                      (totalTimeSpentRef.current || 0) / 60
                    }
                    text={'Còn'}
                    paused={isPaused}
                    onTimeUp={handleTimeUp}
                  />
                ) : (
                  <Timer
                    key={`${currentChapterId}-${totalTimeSpent}-${restudyKey}`}
                    timelimit={0.1 - (totalTimeSpentRef.current || 0) / 60}
                    text={'Còn'}
                    paused={isPaused}
                    onTimeUp={handleTimeUp}
                  />
                )}
                <button
                  onClick={handleTogglePause}
                  disabled={isDailyLimitReached}
                  title={isDailyLimitReached ? 'Đã đạt giới hạn học hôm nay' : isManualPaused ? 'Tiếp tục' : 'Tạm dừng'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    border: `1.5px solid ${isPaused ? '#6ee7b7' : '#93c5fd'}`,
                    background: isPaused
                      ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
                      : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                    color: isPaused ? '#065f46' : '#1e40af',
                    fontSize: 12,
                    cursor: isDailyLimitReached ? 'not-allowed' : 'pointer',
                    padding: 0,
                    opacity: isDailyLimitReached ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isPaused ? '▶' : '❚❚'}
                </button>
                {/* <button
                  onClick={() => openRestudyModal()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '6px 14px',
                    borderRadius: 50,
                    border: '1.5px solid #fca5a5',
                    background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                    color: '#dc2626',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ↻ Học lại
                </button> */}
                <button
                  onClick={() => {
                    setAutoPageTurning((prev) => {
                      const next = !prev;
                      localStorage.setItem('book_auto_page', String(next));
                      return next;
                    });
                  }}
                  title={autoPageTurning ? 'Tắt tự động chuyển chương' : 'Bật tự động chuyển chương'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 20,
                    border: `1.5px solid ${autoPageTurning ? '#6ee7b7' : '#d1d5db'}`,
                    background: autoPageTurning
                      ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
                      : '#f9fafb',
                    color: autoPageTurning ? '#065f46' : '#6b7280',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {autoPageTurning ? '▶ Tự động' : '▷ Tự động'}
                </button>
              </div>
            ) : isReallyCompleted ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 14px',
                    borderRadius: 50,
                    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                    border: '1.5px solid #6ee7b7',
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#065f46' }}>
                    Đã hoàn thành
                  </span>
                </div>
                {/* <button
                  onClick={() => openRestudyModal()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '6px 14px',
                    borderRadius: 50,
                    border: '1.5px solid #fca5a5',
                    background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                    color: '#dc2626',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  ↻ Học lại
                </button> */}
                <button
                  onClick={() => {
                    setAutoPageTurning((prev) => {
                      const next = !prev;
                      localStorage.setItem('book_auto_page', String(next));
                      return next;
                    });
                  }}
                  title={autoPageTurning ? 'Tắt tự động chuyển chương' : 'Bật tự động chuyển chương'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 20,
                    border: `1.5px solid ${autoPageTurning ? '#6ee7b7' : '#d1d5db'}`,
                    background: autoPageTurning
                      ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)'
                      : '#f9fafb',
                    color: autoPageTurning ? '#065f46' : '#6b7280',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {autoPageTurning ? '▶ Tự động' : '▷ Tự động'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '12px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Pagination className='mb-0' style={{ margin: 0 }}>
              <Pagination.Prev
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((prev) => prev - 1)}
              >
                Chương trước
              </Pagination.Prev>
              <Pagination.Item disabled style={{ pointerEvents: 'none' }}>
                {currentIndex + 1} / {fileUrls.length}
              </Pagination.Item>
              <Pagination.Next
                disabled={currentIndex === fileUrls.length - 1}
                onClick={() => setCurrentIndex((prev) =>
                  prev < fileUrls.length - 1 ? prev + 1 : prev
                )}
              >
                Chương sau
              </Pagination.Next>
            </Pagination>
          </div>
        </div>
      </Container>
    </Styles>
  );
};

export default ElearningStudentBookPage;

const Styles = styled.div`
  .video-js {
    width: 100%;
    height: revert;
    background-color: transparent;
  }
`;
